import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Stethoscope, Siren, TestTube, Syringe, CalendarCheck, Clock, RotateCcw, Printer, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { LanguageToggle } from "../components/Brand";

const ICONS = {
  stethoscope: Stethoscope,
  siren: Siren,
  "test-tube": TestTube,
  syringe: Syringe,
  "calendar-check": CalendarCheck,
};

export default function Kiosk() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [creating, setCreating] = useState(false);
  const [clock, setClock] = useState(new Date());

  useEffect(() => {
    api.get(`/public/tenant/${slug}`).then(({ data }) => setData(data));
  }, [slug]);

  useEffect(() => {
    const id = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const take = async (service) => {
    if (creating) return;
    setCreating(true);
    try {
      const { data: tk } = await api.post(`/public/tenant/${slug}/tickets`, { service_id: service.id });
      setTicket(tk);
    } finally {
      setCreating(false);
    }
  };

  const reset = () => setTicket(null);

  const timeStr = useMemo(() => clock.toLocaleTimeString(lang === "fr" ? "fr-FR" : "en-US", { hour: "2-digit", minute: "2-digit" }), [clock, lang]);

  if (!data) return <div className="min-h-screen grid place-items-center text-slate-500">…</div>;

  if (ticket) {
    const qrValue = `FILEPRO|${slug}|${ticket.id}|${ticket.number}`;
    const dateStr = new Date(ticket.created_at || Date.now()).toLocaleString(lang === "fr" ? "fr-FR" : "en-US", { dateStyle: "short", timeStyle: "short" });
    return (
      <div className="min-h-screen bg-white grid place-items-center p-6">
        <div className="ticket-container w-full max-w-2xl bg-white rounded-3xl border border-slate-200 shadow-lg p-8 sm:p-12 text-center fade-in-up">
          <div className="small-text text-sm uppercase tracking-[0.25em] text-blue-700 font-medium">{data.tenant.name}</div>
          <div className="small-text text-xs text-slate-500 mt-1">{t("your_ticket")} · {dateStr}</div>

          <div className="divider hidden print:block" aria-hidden="true" />

          <div className="ticket-number mt-3 font-heading text-[18vw] sm:text-9xl font-extrabold text-slate-900 leading-none tracking-tighter" data-testid="kiosk-ticket-number">
            {ticket.number}
          </div>
          <div className="service mt-2 text-2xl text-slate-700 font-heading">
            {lang === "fr" ? ticket.service_name_fr : ticket.service_name_en}
          </div>

          <div className="no-print mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-800 border border-blue-100">
            <Clock className="w-4 h-4" />
            {t("estimated_wait")}: <span className="font-semibold">{ticket.estimated_wait_min} {t("minutes")}</span>
          </div>
          <div className="small-text hidden print:block mt-2">
            {t("estimated_wait")}: {ticket.estimated_wait_min} {t("minutes")}
          </div>

          <div className="qr mt-8 flex justify-center">
            <div className="p-4 bg-white rounded-2xl border border-slate-200 print:p-0 print:border-0">
              <QRCodeSVG value={qrValue} size={168} bgColor="#ffffff" fgColor="#0F172A" />
            </div>
          </div>
          <div className="small-text mt-3 text-xs text-slate-500">{t("scan_qr")}</div>

          <div className="divider hidden print:block" aria-hidden="true" />

          <div className="small-text mt-8 text-slate-600">{t("please_wait_called")}</div>
          <div className="small-text mt-1 text-xs text-slate-500 hidden print:block">FilePro · filepro.app</div>

          <div className="no-print mt-8 flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-slate-300 text-slate-800 hover:bg-slate-50"
              data-testid="kiosk-print-btn"
            >
              <Printer className="w-4 h-4" /> {t("print_ticket")}
            </button>
            <button
              onClick={reset}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-blue-700 text-white hover:bg-blue-800"
              data-testid="kiosk-new-ticket-btn"
            >
              <RotateCcw className="w-4 h-4" /> {t("new_ticket")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-blue-700 font-medium">{t("kiosk")}</div>
            <div className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">{data.tenant.name}</div>
          </div>
          <div className="flex items-center gap-4">
            <div className="font-heading text-2xl text-slate-700">{timeStr}</div>
            <LanguageToggle />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="text-center mb-10">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">{t("select_service")}</h1>
          <p className="mt-3 text-slate-600 text-lg">{t("take_ticket")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.services.map((s) => {
            const Icon = ICONS[s.icon] || Stethoscope;
            const isUrgence = s.prefix === "U";
            return (
              <button
                key={s.id}
                onClick={() => take(s)}
                data-testid={`kiosk-btn-${s.prefix.toLowerCase()}`}
                className="kiosk-btn group relative text-left rounded-3xl bg-white border-2 p-8 min-h-[220px] flex flex-col justify-between shadow-lg hover:shadow-xl transition"
                style={{ borderColor: s.color }}
              >
                {isUrgence && (
                  <span className="absolute top-4 right-4 inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold text-white pulse-soft" style={{ backgroundColor: s.color }}>
                    {t("priority")}
                  </span>
                )}
                <div className="w-16 h-16 rounded-2xl grid place-items-center" style={{ backgroundColor: `${s.color}1A`, color: s.color }}>
                  <Icon className="w-9 h-9" strokeWidth={2.5} />
                </div>
                <div className="mt-6">
                  <div className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900">
                    {lang === "fr" ? s.name_fr : s.name_en}
                  </div>
                  <div className="mt-2 text-slate-500 text-sm uppercase tracking-widest">
                    {t("ticket_number")}: {s.prefix}-***
                  </div>
                </div>
                <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium" style={{ color: s.color }}>
                  {t("take_ticket")} <ArrowRight className="w-4 h-4" />
                </div>
              </button>
            );
          })}
        </div>
      </main>
    </div>
  );
}
